import type { Traveller } from './live-frame';
import type { Random } from './rng';
import { mulberry32 } from './rng';

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * MOCKS. NOTHING IN THIS FILE IS TRUE.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Every function here invents data the app does not have yet, so that the layers which draw that
 * data can be SEEN and judged against the reference. The reference does exactly the same thing and
 * says so in its own index: "300 real names + 9,700 synthetic filler". The difference is that these
 * are in one file, with one flag, and named after what has to replace them.
 *
 * HOW TO FIND AND REMOVE THEM
 *
 *   1. `grep -rn "map/v8/mocks" web/src` — every caller, in one list.
 *   2. Each function below names the real source it stands in for, under REPLACE WITH.
 *   3. Delete the function, delete its call, delete its row in `V8-PORT.md` §Mocks.
 *
 * THEY ARE OFF UNLESS ASKED FOR. `mocksWanted()` reads `?mock=1` from the address bar and nothing
 * else — no environment variable, no build flag, no "development only" check that turns out to be
 * true in a preview build. A mock that is on by default is a mock that ships, and this project has
 * a standing rule that held-back means not shipped. The flag is the whole of the safety.
 */

/** Whether the reader has explicitly asked to see invented data. Nothing else turns these on. */
export function mocksWanted(search: string): boolean {
  return new URLSearchParams(search).get('mock') === '1';
}

/**
 * READERS IN TRANSIT, INVENTED.
 *
 * REPLACE WITH: the social plane. A traveller is a reader who has opened one name and then opened
 * another, which is a fact the comms layer could report and `usePresence` already has the shape for
 * — see `server/src/social/rooms/rooms.service.ts`, which today reports occupancy per node and no
 * movement at all. Nothing in the library says a reader is moving; only the server can.
 *
 * WHY IT MATTERS ENOUGH TO FAKE: their canvas is busy with these at every range, and the map reads
 * as inhabited because of them. Ours draws none, so `drawTravellers` — ported and differentially
 * tested — has never once been seen doing its job outside a test.
 *
 * Deterministic from a seed, so a screenshot taken twice is the same screenshot.
 */
export function mockTravellers(
  edges: readonly (readonly [number, number])[],
  colours: readonly string[],
  count: number,
  seconds: number,
  seed = 20260819,
): Traveller[] {
  if (edges.length === 0 || colours.length === 0) return [];
  const random: Random = mulberry32(seed);
  const out: Traveller[] = [];
  for (let i = 0; i < count; i++) {
    const edge = edges[Math.floor(random() * edges.length)];
    if (edge === undefined) continue;
    const colour = colours[Math.floor(random() * colours.length)] ?? '#63e0e8';
    /* Each walks at its own pace and starts where its seed put it, so they do not march in step. */
    const pace = 0.05 + random() * 0.12;
    const offset = random();
    out.push({ a: edge[0], b: edge[1], t: (offset + seconds * pace) % 1, c: colour });
  }
  return out;
}

/**
 * WHO IS STANDING WHERE, INVENTED.
 *
 * REPLACE WITH: `usePresence`, which is real, wired, and returns nothing here only because this
 * preview has no social plane behind it. When a plane is present the real counts arrive and this
 * must go — the shapes are deliberately the same so the swap is a deletion rather than a rewrite.
 *
 * COUNTS ONLY, NEVER NAMES, exactly as the real one is. Being in a public room is consent to be in
 * that room rather than to be plotted, and a mock that invented handles would be teaching the map a
 * habit the real plane must never have.
 */
/**
 * THEIR RULE, NOT A NEW ONE — engine.js:379.
 *
 *     pop: Math.max(0, Math.round((s.w - 3) * (1 + rnd() * 3)))
 *
 * A name's readers follow its WEIGHT. Every notable figure has somebody standing at it and the
 * obscure ones have nobody, which is why their map reads as inhabited where it matters and why
 * opening a famous name on theirs always shows a ring of readers.
 *
 * OURS USED TO SCATTER PRESENCE OVER 300 NAMES AT RANDOM out of 1,099, which is a different rule
 * wearing the same word. Its visible cost: a reader opens Abraham or Adam and finds nobody there
 * about three times in four, so the seats and the chips almost never appeared on the name being
 * looked at. The count 300 was invented; this is ported.
 *
 * THEIR WEIGHTS RUN 0-10 AND OURS RUN HIGHER, so the weight is read against our own top before
 * their arithmetic is applied — the same normalisation `THEIR_NEED` and `labelWeightsFor` already
 * do, and for the same reason: a weight means nothing without the scale it is counted on.
 */
const THEIR_TOP_WEIGHT = 10;
const THEIR_POP_FLOOR = 3;
const THEIR_POP_SPREAD = 3;

/**
 * WHO IS STANDING WHERE, INVENTED.
 *
 * REPLACE WITH: `usePresence`, which polls the real thing and returns the same shape. When it
 * must go — the shapes are deliberately the same so the swap is a deletion rather than a rewrite.
 *
 * COUNTS ONLY, NEVER NAMES, exactly as the real one is. Being in a public room is consent to be in
 * that room rather than to be plotted, and a mock that invented handles here would be teaching the
 * map a habit the real plane must never have. `mockReadersAt` is where handles live, separately,
 * so that the day presence is real this one is deleted without taking the question with it.
 */
export function mockPresence(
  roster: readonly { id: string; w: number }[],
  seed = 981_11,
): Map<string, { readers: number; voice: boolean }> {
  const random: Random = mulberry32(seed);
  const out = new Map<string, { readers: number; voice: boolean }>();
  const top = Math.max(1, ...roster.map(n => n.w));
  for (const n of roster) {
    /* Their weight, so their thresholds mean here what they mean there. */
    const theirs = (n.w / top) * THEIR_TOP_WEIGHT;
    const readers = Math.max(0, Math.round((theirs - THEIR_POP_FLOOR) * (1 + random() * THEIR_POP_SPREAD)));
    if (readers <= 0) continue;
    out.set(n.id, { readers, voice: random() < 0.12 });
  }
  return out;
}

/*
 * SOMEBODY ELSE READING, INVENTED — GONE, on Ariel's instruction, 2026-08-22.
 *
 * A card under the search, every 9.5 seconds for 5.4 of them: `reader_8` over *"the original
 * splits a word here in a way the English cannot."* `mockAmbient`, `AMBIENT_REMARKS`,
 * `AmbientRemark`, `Ambient`, `AMBIENT_TIMING`, the `MapAmbient` component that showed it and the
 * six `ambient.*` phrases in three languages, all removed.
 *
 * IT WENT ONE ASK AFTER THE ACTIVITY FEED, and the two were separate features that looked like one
 * thing to the person watching: both put `reader_N` on the screen in the same corner. The feed was
 * removed first and the remark went on appearing, which is why this is worth writing down — "drop
 * the thing showing reader_N" named two features, and answering it once left the reported fault
 * exactly where it was.
 *
 * WHY IT EXISTED, kept because the observation is right and outlives the mock: their arrival lands
 * a reader on Adam and Genesis 4:1, and within three seconds somebody else asks whether "knew" is
 * carrying what the Hebrew carries. Ours flies the same reader over the field, names three regions
 * and sets them down — alone, in a library nobody else appears to be in. The layouts differ by
 * design and that is not the difference: one map has people in it from the first second.
 *
 * REPLACE WITH: the comms layer. A remark on a passage is a post, and `server/src/social/` already
 * carries posts — what is missing is a reason to surface one over the map rather than in a thread.
 * Whoever does it inherits the two rules this obeyed. The remarks were KEYS and never sentences,
 * because a remark about how a translation reads is the last thing that should reach an Arabic
 * reader in English. And every one was about READING rather than about a figure, which is what let
 * them appear anywhere on the map without lying about what was under the pointer.
 */

/*
 * THE ACTIVITY FEED IS GONE, on Ariel's instruction, 2026-08-22: *"drop the feature that shows a
 * popup under the search showing 'reader x' with some text underneath"*.
 *
 * It invented four lines of `reader_27 left a note on <figure>` and showed them in the corner
 * cluster under the search, behind `?mock=1` — `mockActivity`, `FEED_DEEDS`, `FeedDeed` and
 * `FeedEntry`, all removed, along with the cluster's prop and the six `feed.<deed>` phrases in
 * three languages. Removed rather than left inert: a generator nothing calls and a prop nothing
 * draws is the shape this repository keeps finding shipped and unreachable.
 *
 * WHAT IT WAS FOR, kept because the question outlives the mock: the reference has a feed, the
 * layout could not be judged without one, and whether the REAL feed may name anybody was always an
 * open decision rather than something this settled. The map's presence layer carries counts and
 * never names, deliberately — being in a public room is consent to be in that room rather than to
 * be plotted — and a feed saying who read what is a stronger claim than that. Whoever builds the
 * real one inherits that question unanswered, which is where it was before this existed.
 *
 * Two smaller things worth carrying to whatever replaces it. The deeds were KEYS and never
 * sentences, because the dictionary owns the words in three languages and one of them is right to
 * left — a feed of English literals would have been the one part of the interface unreadable in
 * Arabic. And the handles were `reader_1` rather than plausible names, so no screenshot could pass
 * for real; that reasoning belongs to any mock that draws people, and the reader chips still do.
 */

/*
 * THERE WAS A FRONTIER MOCK HERE, and it is gone by a rule rather than by a bug — Ariel, 2026-08-21:
 *
 *   "the colorful nodes must be only actual nodes, don't add fake nodes it makes the whole thing
 *    cheaper. it reduces the impact of how many content we have if everything is filled with fake
 *    nodes"
 *
 * It scattered 8,900 unnamed lights past the rim, because theirs draws 10,000 names where we have
 * 1,099 and the far field looked thin beside it. Three shapes were tried in one afternoon: spread
 * over the whole disc, which put 426 of them among the names a reader came for; cut off at the
 * crowd's edge, which drew a bright circular BAND round the map and was the version Ariel saw and
 * asked about; and biased outward, which had no edge and still filled the sky with nobody.
 *
 * Every one of them was a better answer to the wrong question. 1,099 real names IS the fact, and
 * padding the field to look like ten thousand tells a reader the opposite of what this library has
 * actually done. Theirs is a prototype padded with `Seth·227` clones; ours is the roster.
 *
 * DO NOT BRING IT BACK to close the density gap with theirs. The gap is real, it is measured — ink
 * 2 against their 29 at the far range — and it is the honest number.
 */

export function mockReadersAt(seed = 6021): (nodeId: number, cap: number) => string[] {
  return (nodeId, cap) => {
    /* Deterministic per name, as theirs is: the same figure has the same people at it all
       session, because handles that reshuffled every frame would read as a fault. */
    const random: Random = mulberry32(seed + nodeId * 7919);
    const many = Math.max(0, Math.floor(cap));
    const out: string[] = [];
    for (let i = 0; i < many; i++) {
      const who = `reader_${1 + Math.floor(random() * 40)}`;
      if (!out.includes(who)) out.push(who);
    }
    return out;
  };
}

/**
 * HOW MANY PEOPLE ARE READING, INVENTED.
 *
 * REPLACE WITH: a sum over `usePresence`, which already reports occupancy per figure — the total is
 * one reduce away the moment a plane is behind it. Their number moves as you watch; this one is
 * steady, because a number that ticks upward on a mock is a number somebody will believe.
 */
export function mockExploring(seed = 771): number {
  return 900 + Math.floor(mulberry32(seed)() * 700);
}

/** One room with a live voice in it. */
export interface MockRoom {
  name: string;
  readers: number;
}

/**
 * ROOMS WITH A VOICE IN THEM, INVENTED.
 *
 * REPLACE WITH: `topRooms` over the rooms service, which is real, has occupancy, and knows which
 * rooms carry live audio — `server/src/social/rooms/rooms.service.ts`. It returns nothing here
 * only because this preview has no plane behind it.
 */
export function mockRooms(names: readonly string[], howMany: number, seed = 33_113): MockRoom[] {
  if (names.length === 0) return [];
  const random: Random = mulberry32(seed);
  const out: MockRoom[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < howMany * 4 && out.length < howMany; i++) {
    const name = names[Math.floor(random() * names.length)];
    if (name === undefined || seen.has(name)) continue;
    seen.add(name);
    out.push({ name, readers: 8 + Math.floor(random() * 45) });
  }
  return out;
}
